import { useEffect, useState } from "react";
import api from "@/lib/api";

/**
 * Business landing — the morning operational dashboard.
 *
 * Data contract: GET /api/admin/business-summary.
 * Timezone: Australia/Sydney. Week starts Monday.
 * Customer-only filter applied to all customer/project/render counts.
 * Stripe revenue is LIVE and never filtered.
 *
 * Each card declares its data source via a `sub` line + `data-testid`.
 */

function BusinessCard({ testId, label, value, sub, accent }) {
  const accentClass =
    accent === "amber"
      ? "border-amber-300 bg-amber-50"
      : accent === "red"
      ? "border-red-300 bg-red-50"
      : "border-brand-linen bg-white";
  return (
    <div data-testid={testId} className={`border p-6 ${accentClass}`}>
      <div className="overline mb-2">{label}</div>
      <div className="font-serif text-3xl text-brand-ink tracking-tight">{value}</div>
      {sub && <div className="text-xs text-brand-muted mt-2">{sub}</div>}
    </div>
  );
}

function HealthDot({ status }) {
  const color =
    status === "ok"
      ? "bg-brand-success"
      : status === "not_configured"
      ? "bg-gray-300"
      : "bg-red-500";
  return <span className={`inline-block w-2.5 h-2.5 rounded-full mr-2 ${color}`} />;
}

function relTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return `${Math.floor(diff)}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

function money(n) {
  return `$${(n ?? 0).toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export default function AdminBusiness() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    let alive = true;
    api
      .get("/admin/business-summary")
      .then((r) => alive && setData(r.data))
      .catch((e) => alive && setErr(e.message || "Failed to load"));
    return () => {
      alive = false;
    };
  }, []);

  if (err) {
    return (
      <div data-testid="admin-business-error" className="text-sm border border-destructive/30 bg-destructive/5 text-destructive px-4 py-3">
        Could not load business summary: {err}
      </div>
    );
  }
  if (!data) {
    return <div data-testid="admin-business-loading" className="text-sm text-brand-muted">Loading business summary…</div>;
  }

  const h = data.headline;
  const failedAccent =
    h.failed_renders_24h >= 5 ? "red" : h.failed_renders_today > 0 ? "amber" : null;

  return (
    <div data-testid="admin-business">
      {/* Header */}
      <div className="flex items-baseline justify-between mb-6">
        <div className="overline">Today · {data.today_date} · {data.timezone}</div>
        {data.system_health?.maintenance_banner && (
          <div data-testid="business-maintenance-banner" className="text-xs px-3 py-1 bg-amber-100 text-amber-900 border border-amber-200">
            Maintenance: {data.system_health.maintenance_banner}
          </div>
        )}
      </div>

      {/* Revenue */}
      <div className="overline mb-3">Revenue</div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
        <BusinessCard
          testId="business-card-revenue-today"
          label="Revenue · today"
          value={money(h.revenue_today_aud)}
          sub="AUD · live Stripe · AEST"
        />
        <BusinessCard
          testId="business-card-revenue-week"
          label="Revenue · this week"
          value={money(h.revenue_week_aud)}
          sub="AUD · live Stripe · since Mon"
        />
        <BusinessCard
          testId="business-card-revenue-month"
          label="Revenue · this month"
          value={money(h.revenue_month_aud)}
          sub="AUD · live Stripe"
        />
        <BusinessCard
          testId="business-card-paid-unlocks"
          label="Paid unlocks · today"
          value={h.paid_unlocks_today}
          sub={`Week ${h.paid_unlocks_week} · Month ${h.paid_unlocks_month} · live Stripe`}
        />
      </div>

      {/* Customers + activity */}
      <div className="overline mb-3">Customers</div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
        <BusinessCard
          testId="business-card-new-customers"
          label="New customers · today"
          value={h.new_customers_today}
          sub={`Week ${h.new_customers_week} · Month ${h.new_customers_month} · customers only`}
        />
        <BusinessCard
          testId="business-card-active-customers"
          label="Active customers · 24h"
          value={h.active_customers_24h}
          sub={"v1 definition · see tooltip"}
        />
        <BusinessCard
          testId="business-card-projects-today"
          label="Projects created · today"
          value={h.projects_today}
          sub={`Week ${h.projects_week} · Month ${h.projects_month} · customers only`}
        />
        <BusinessCard
          testId="business-card-renders-today"
          label="Renders generated · today"
          value={h.renders_today}
          sub="status=ready · customers only · AEST"
        />
      </div>

      {/* Failed + cost + profit */}
      <div className="overline mb-3">Cost &amp; quality</div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-2">
        <BusinessCard
          testId="business-card-failed-renders"
          label="Failed renders · today"
          value={h.failed_renders_today}
          sub={`24h: ${h.failed_renders_24h} · customers only`}
          accent={failedAccent}
        />
        <BusinessCard
          testId="business-card-ai-cost-today"
          label="AI cost · today (est.)"
          value={money(h.ai_cost_today_aud)}
          sub={`estimated at $${(h.ai_cost_per_render_aud ?? 0).toFixed(2)}/render · not actual Gemini billing`}
        />
        <BusinessCard
          testId="business-card-profit-today"
          label="Profit · today (est.)"
          value={money(h.profit_estimate_today_aud)}
          sub="revenue − est. AI cost · excludes Stripe fees (M4) and hosting"
        />
        <BusinessCard
          testId="business-card-internal-excluded"
          label="Internal excluded"
          value={data.internal_user_count}
          sub="owner/admin accounts excluded from customer metrics"
        />
      </div>
      <div data-testid="business-active-customers-definition" className="text-xs text-brand-muted mb-10">
        <strong>Active customer definition (v1):</strong> {data.active_customers_definition}
      </div>

      {/* Activity feeds */}
      <div className="grid md:grid-cols-2 gap-6 mb-10">
        <div data-testid="business-recent-purchases" className="bg-white border border-brand-linen p-6">
          <div className="overline mb-4">Recent purchases · live Stripe</div>
          {data.recent_purchases.length === 0 ? (
            <div className="text-sm text-brand-muted">No customer purchases yet.</div>
          ) : (
            <ul className="space-y-3">
              {data.recent_purchases.map((p) => (
                <li
                  key={p.tx_id}
                  data-testid={`recent-purchase-${p.tx_id}`}
                  className="flex items-baseline justify-between text-sm border-b border-brand-linen/60 pb-2 last:border-0"
                >
                  <div className="flex-1 truncate pr-3">
                    <div className="font-serif text-brand-ink">{money(p.amount)}</div>
                    <div className="text-xs text-brand-muted truncate">{p.customer_email || "—"}</div>
                  </div>
                  <div className="text-xs text-brand-muted whitespace-nowrap">{relTime(p.created_at)}</div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div data-testid="business-recent-support" className="bg-white border border-brand-linen p-6">
          <div className="overline mb-4">Recent support tickets · live</div>
          {data.recent_support.length === 0 ? (
            <div className="text-sm text-brand-muted">No support tickets yet — the inbox is clear.</div>
          ) : (
            <ul className="space-y-3">
              {data.recent_support.map((t) => (
                <li
                  key={t.ticket_id}
                  data-testid={`recent-ticket-${t.ticket_id}`}
                  className="flex items-baseline justify-between text-sm border-b border-brand-linen/60 pb-2 last:border-0"
                >
                  <div className="flex-1 truncate pr-3">
                    <div className="font-serif text-brand-ink truncate">{t.subject || "(no subject)"}</div>
                    <div className="text-xs text-brand-muted truncate">
                      {t.status?.toUpperCase()} · {t.customer_email || "—"}
                    </div>
                  </div>
                  <div className="text-xs text-brand-muted whitespace-nowrap">{relTime(t.created_at)}</div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* System health */}
      <div data-testid="business-system-health" className="bg-white border border-brand-linen p-6">
        <div className="overline mb-4">System health · live</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div data-testid="health-backend">
            <HealthDot status={data.system_health.backend} />
            Backend · {data.system_health.backend}
          </div>
          <div data-testid="health-mongo">
            <HealthDot status={data.system_health.mongo} />
            Mongo · {data.system_health.mongo}
          </div>
          <div data-testid="health-stripe">
            <HealthDot status={data.system_health.stripe_api} />
            Stripe API · {data.system_health.stripe_api}
          </div>
          <div data-testid="health-render-failure">
            <span className="inline-block w-2.5 h-2.5 rounded-full mr-2 bg-brand-navy" />
            Render failures (24h) · {data.system_health.render_failure_rate_24h_pct}%
          </div>
        </div>
      </div>
    </div>
  );
}
